import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { trackedFunction } from 'reactiveweb/function';
import { Button } from 'tio-ui/components/buttons';
import { VStack, Section } from 'tio-ui/components/layout';
import { eq } from 'tio-ui/utilities';
import {
  PhFloppyDisk,
  PhDownload,
  PhTable,
  PhChartBar,
  PhChartPie,
  PhChartLine,
  PhChartLineUp,
  PhTrash,
} from 'ember-phosphor-icons';
import SaveReportDialog from './save-report-dialog.gts';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type Report from '../models/reports';
import { ReportLoader, type ReportLoadContext } from '../utils/report-loader';
import { queryReportsRequest } from '../requests/report';

interface SavedReportsDrawerSignature {
  Args: {
    onLoad: (report: Report) => void;
    onSave?: () => void;
    onExport?: () => void;
    onDelete?: (report: Report) => void;
    // Required for SaveReportDialog
    searchData?: Record<string, unknown>;
    serializedQuery?: string;
    reportType?: 'chart_builder' | 'table_builder';
    currentReport?: Report;

    // Whether to show partner visibility options (only for global/cross-company context)
    showPartnerVisibility?: boolean;

    // Context functions for ReportLoader
    setDataset?: (dataset: string) => void;
    setGroupByField?: (field: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setChartType?: (type: any) => void;
    setTimeRange?: (range: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDateGrouping?: (grouping: any) => void;
    setShowTableView?: (show: boolean) => void;
    clearQueryCache?: () => void;
    updateQueryParameters?: (params: Record<string, unknown>) => void;
    setCurrentReport?: (report: Report) => void;
  };
}

export default class SavedReportsDrawer extends Component<SavedReportsDrawerSignature> {
  @service declare store: StoreService;

  @tracked selectedReportId = '';
  @tracked showSaveDialog = false;

  reportsRequest = trackedFunction(this, async () => {
    const { content } = await this.store.request(queryReportsRequest());
    return (content as { data: Report[] }).data || [];
  });

  get defaultReportType(): 'chart_builder' | 'table_builder' {
    return this.args.reportType || 'chart_builder';
  }

  get defaultSearchData() {
    return this.args.searchData || {};
  }

  getReportIcon(report: Report) {
    // Table builder reports get table icons
    if (report.isTableBuilder) {
      return PhTable;
    }

    // For chart builder reports, try to get specific chart type from chartSettings
    const searchConfig = report.searchConfig as {
      chartSettings?: { selectedType?: string };
    };
    const chartType = searchConfig?.chartSettings?.selectedType;

    // If we have a specific chart type, show the appropriate icon
    if (chartType) {
      switch (chartType) {
        case 'bar':
        case 'horizontalBar':
          return PhChartBar;
        case 'pie':
        case 'donut':
          return PhChartPie;
        case 'line':
        case 'area':
          return PhChartLine;
      }
    }

    // Default chart icon for chart builders
    return PhChartLineUp;
  }

  get availableReports() {
    // Access the reportsRequest to trigger loading
    return this.reportsRequest.value || [];
  }

  get isLoading() {
    return this.reportsRequest.isLoading;
  }

  get hasError() {
    return this.reportsRequest.isError;
  }

  get reportsList() {
    return this.availableReports.map((report) => ({
      id: report.id,
      name: report.displayName,
      type: report.isTableBuilder ? 'Table' : 'Chart',
      visibility: report.visibilityLabel,
      isPrivate: report.isPrivate,
      updatedAt: report.updatedAt,
      report: report,
      icon: this.getReportIcon(report),
    }));
  }

  get selectedReport() {
    return this.availableReports.find((r) => r.id === this.selectedReportId);
  }

  get hasReports() {
    return this.reportsList.length > 0;
  }

  selectReport = (reportId: string) => {
    this.selectedReportId = reportId;
    // Auto-load the report when selected (like original dropdown behavior)
    this.loadSelectedReport();
  };

  loadSelectedReport = async () => {
    const report = this.selectedReport;
    if (!report) return;

    // Build context from provided functions
    const context: ReportLoadContext = {
      setDataset: this.args.setDataset,
      setGroupByField: this.args.setGroupByField,
      setChartType: this.args.setChartType,
      setTimeRange: this.args.setTimeRange,
      setDateGrouping: this.args.setDateGrouping,
      setShowTableView: this.args.setShowTableView,
      clearQueryCache: this.args.clearQueryCache,
      updateQueryParameters: this.args.updateQueryParameters,
      setCurrentReport: this.args.setCurrentReport,
    };

    try {
      const result = await ReportLoader.loadReport(report, context);
      if (result.success) {
        console.log('Successfully loaded report:', report.displayName);
      } else {
        console.error('Failed to load report:', result.error);
      }
      // Always call onLoad callback to notify parent (for any additional handling)
      this.args.onLoad(report);
    } catch (error) {
      console.error('Error loading report with ReportLoader:', error);
      // Still call onLoad callback as fallback
      this.args.onLoad(report);
    }
  };

  handleSaveReport = () => {
    this.showSaveDialog = true;
  };

  handleCloseSaveDialog = () => {
    this.showSaveDialog = false;
  };

  handleSaveDialogSave = (_report: Report) => {
    // Note: Dialog closes automatically via SaveReportDialog.onSubmit -> this.args.onClose()
    this.reportsRequest.retry(); // Refresh the reports list
    this.args.onSave?.(); // Notify parent if needed
  };

  handleExportCSV = () => {
    this.args.onExport?.();
  };

  handleDelete = async (report: Report) => {
    if (!report.id) return;

    if (confirm(`Are you sure you want to delete "${report.displayName}"?`)) {
      try {
        await report.destroyRecord();
        this.args.onDelete?.(report);

        // Clear selection if deleted report was selected
        if (this.selectedReportId === report.id) {
          this.selectedReportId = '';
        }

        // Refresh the list
        this.reportsRequest.retry();
      } catch (error) {
        console.error('Failed to delete report:', error);
      }
    }
  };

  <template>
    <VStack class="h-full">
      {{! Top section with Load & Save Report and action buttons }}
      <Section>
        <:actions>
          <Button
            @appearance="outlined"
            @intent="success"
            @icon={{PhFloppyDisk}}
            @size="sm"
            {{on "click" this.handleSaveReport}}
          >
            Save Report
          </Button>
          <Button
            @appearance="outlined"
            @intent="primary"
            @icon={{PhDownload}}
            @size="sm"
            {{on "click" this.handleExportCSV}}
          >
            Export CSV
          </Button>
        </:actions>
      </Section>

      {{! Reports section }}
      <div class="flex-1">
        {{#if this.isLoading}}
          <Section>
            <:body>
              <div class="text-center text-gray-400">Loading saved reports...</div>
            </:body>
          </Section>
        {{else if this.hasError}}
          <Section>
            <:body>
              <div class="text-center text-red-200">Error loading reports. Check console.</div>
            </:body>
          </Section>
        {{else if this.hasReports}}
          <VStack @collapsed={{true}}>
            {{#each this.reportsList as |item|}}
              <Section
                class="transition-colors group
                  {{if
                    (eq this.selectedReportId item.id)
                    'bg-blue-600 text-white'
                    'text-gray-300 hover:bg-gray-700'
                  }}"
              >
                <div class="flex justify-between items-center">
                  <button
                    type="button"
                    class="flex-1 p-3 text-left bg-transparent border-none cursor-pointer"
                    {{on "click" (fn this.selectReport item.id)}}
                  >
                    <div class="flex items-center gap-3">
                      {{#let item.icon as |IconComponent|}}
                        <IconComponent @size="20" class="flex-shrink-0" />
                      {{/let}}
                      <div>
                        <div class="font-medium">{{item.name}}</div>
                        <div class="text-sm opacity-75">{{item.type}}
                          •
                          {{item.visibility}}</div>
                      </div>
                    </div>
                  </button>
                  <Button
                    @size="sm"
                    @intent="danger"
                    @appearance="outlined"
                    @icon={{PhTrash}}
                    {{on "click" (fn this.handleDelete item.report)}}
                    data-test-delete-report={{item.id}}
                    class="flex"
                  />
                </div>
              </Section>
            {{/each}}
          </VStack>

        {{else}}
          <Section>
            <:body>
              <div class="text-center">
                <PhTable @size="48" class="mx-auto text-gray-500 mb-4" />
                <div class="text-gray-400">
                  No saved reports found.
                  <br />
                  Create your first saved report!
                </div>
              </div>
            </:body>
          </Section>
        {{/if}}
      </div>
    </VStack>

    {{! Save Report Dialog }}
    <SaveReportDialog
      @isOpen={{this.showSaveDialog}}
      @onClose={{this.handleCloseSaveDialog}}
      @onSave={{this.handleSaveDialogSave}}
      @searchData={{this.defaultSearchData}}
      @serializedQuery={{@serializedQuery}}
      @reportType={{this.defaultReportType}}
      @currentReport={{@currentReport}}
      @showPartnerVisibility={{@showPartnerVisibility}}
    />
  </template>
}
